import type { BigFiveQuestionId, BigFiveTrait } from '@llura/types';

/**
 * The 10-item TIPI-style contract: each trait gets one forward + one reverse
 * item. IDs are stable; question wording lives in i18n keyed by the same IDs.
 * Direction is consumed by the API scorer; we keep it client-side too so the
 * UI can render in a fixed shuffled order without two adjacent same-trait items.
 */
export interface BigFiveQuestionMeta {
  id: BigFiveQuestionId;
  trait: BigFiveTrait;
  direction: 'forward' | 'reverse';
}

const ORDERED: readonly BigFiveQuestionMeta[] = [
  { id: 'o1', trait: 'openness', direction: 'forward' },
  { id: 'o2', trait: 'openness', direction: 'reverse' },
  { id: 'c1', trait: 'conscientiousness', direction: 'forward' },
  { id: 'c2', trait: 'conscientiousness', direction: 'reverse' },
  { id: 'e1', trait: 'extraversion', direction: 'forward' },
  { id: 'e2', trait: 'extraversion', direction: 'reverse' },
  { id: 'a1', trait: 'agreeableness', direction: 'forward' },
  { id: 'a2', trait: 'agreeableness', direction: 'reverse' },
  { id: 'n1', trait: 'neuroticism', direction: 'forward' },
  { id: 'n2', trait: 'neuroticism', direction: 'reverse' },
];

/**
 * Interleave traits so no two consecutive questions test the same trait.
 * Same display order for every user so support / debugging stays predictable
 * and analytics outcomes are comparable across sessions.
 *
 * Order: O+, C+, E+, A+, N+, O-, C-, E-, A-, N-
 */
export const BIG_FIVE_QUESTIONS: readonly BigFiveQuestionMeta[] = [
  ORDERED[0],  // o1 — openness forward
  ORDERED[2],  // c1
  ORDERED[4],  // e1
  ORDERED[6],  // a1
  ORDERED[8],  // n1
  ORDERED[1],  // o2 — openness reverse
  ORDERED[3],  // c2
  ORDERED[5],  // e2
  ORDERED[7],  // a2
  ORDERED[9],  // n2
];

export const BIG_FIVE_TOTAL = BIG_FIVE_QUESTIONS.length;
